import React, { useState, useRef } from 'react'
import {
	AppBar, Box, Button, Chip, Dialog, DialogContent, DialogActions, DialogTitle, Grid, LinearProgress, Tab, Tabs,
	TextField, Typography, RadioGroup, FormControlLabel, Radio, Divider, Stack, CircularProgress, Slider
} from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CancelIcon from '@mui/icons-material/Cancel'
import { ClassNameMap, withStyles } from '@mui/styles'
import Draggable from 'react-draggable'
import { toast } from 'react-toastify'
import moment from 'moment'
import stylesheet from '../detail-container.stylesheet'
import { PatientService } from '../../../../services/patient.service'
import Helper from '../../../../utils/helper'
import { useSelector } from 'react-redux'
import { RootState } from '../../../../redux/store'
import EmptyCardComponent from './empty-card.component'
import DialogTitleHeaderComponent from './dialog-title-header.component';

interface IProps {
	open: boolean
	handleClose: any
	handleAction: any
	input: any
	classes: ClassNameMap
}
interface TabPanelProps {
	children?: React.ReactNode
	index: any
	value: any
}

function TabPanel(props: TabPanelProps) {
	const { children, value, index, ...other } = props
	return (<div
		role="tabpanel"
		hidden={value !== index}
		id={`simple-tabpanel-${index}`}
		aria-labelledby={`simple-tab-${index}`}
		{...other}
	>
		{value === index && <>{children}</>}
	</div>
	)
}

const MonitorDialogComponent: React.FC<IProps> = ({ open, handleClose, handleAction, input, classes }) => {
	const [data, setData] = useState(input as any)
	const [loading, setLoading] = useState(false)
	const [loadingEval, setLoadingEval] = useState(false)
	const patientService = new PatientService()
	const monitorRef = useRef(null) as any
	const [isStopMonitor, SetIsStopMonitor] = useState(false)
	const [stopReason, setStopReason] = useState('resolved')
	const [stopNote, setStopNote] = useState('')
	const [noDay, setNoDay] = useState('')
	const [severity, setSeverity] = useState<number>(input?.current_severity || 0)
	const [savingSeverity, setSavingSeverity] = useState(false)
	const patientDetail = useSelector((state: RootState) => state.patientDetail.patientDetail)
	const [configItem] = useState({
		focusTabIndex: 0,
		tabs: [
			{ title: 'Information', index: 0 },
			{ title: 'Evaluation', index: 1 },
		]
	})
	const [value, setValue] = useState(configItem?.focusTabIndex || 0 as number)
	const [daysError, setDaysError] = useState('')
	const handleConfirmMonitorClose = () => {
		SetIsStopMonitor(false)
		setStopReason('resolved')
		setStopNote('')
	}

	const confirmMonitor = () => {
		handleConfirmMonitorClose()
		setLoading(true)
		input?.id && patientService.terminateMonitor(input.id, { reason: stopReason, note: stopNote })
			.then((res: any) => {
				res?.data?.monitor
					? handleAction(input?.id)
					: handleAction()
			})
			.catch((err: any) => {
				toast.error(Helper.getErrorText(err), Helper.bottom_center())
			})
			.finally(() => {
				setLoading(false)
				handleClose()
			})
	}

	const handleInputChange = (e: any) => {
		const noDays = e.target.value
		setNoDay(noDays)
		if (!/^[0-9\b]+$/.test(noDays))
			setDaysError('Please enter an integer.')
		else if (noDays < 1)
			setDaysError('No of days should be atleast 1.')
		else
			setDaysError('')
	}

	const extendMonitor = () => {
		setLoading(true)
		noDay && patientService.extendMonitor(patientDetail.id, input?.id, { extended_day_count: noDay })
			.then((res: any) => {
				setData(res?.data?.monitor)
				setNoDay('')
				toast.success('Monitor extended', Helper.bottom_center())
			})
			.catch((err: any) => toast.error(Helper.getErrorText(err), Helper.bottom_center()))
			.finally(() => setLoading(false))
	}

	const handleMonitorEval = () => {
		setLoadingEval(true)
		patientService.getMonitorvaluation(input?.id).then((response: any) => {
			setData(response?.data?.monitor)
			setLoadingEval(false)
		}).catch(() => {
			setLoadingEval(false)
			toast.error('Evaluation data not available.', Helper.bottom_center())
		})
	}

	const handleSaveSeverity = () => {
		if (!severity || severity < 1) return
		setSavingSeverity(true)
		patientService.updateMonitorSeverity(input?.id, severity)
			.then((res: any) => {
				setData(res?.data?.monitor)
				toast.success('Severity updated', Helper.bottom_center())
			})
			.catch((err: any) => toast.error(Helper.getErrorText(err), Helper.bottom_center()))
			.finally(() => setSavingSeverity(false))
	}

	const handleChange = (_event: any, newValue: React.SetStateAction<number>) => {
		setValue(newValue)
	}

	const a11yProps = (index: number) => ({
		id: `scrollable-auto-tab-${index}`,
		'aria-controls': `scrollable-auto-tabpanel-${index}`,
	})

	// --- Shared helpers ---
	const statusColor = (type: string) => {
		if (type === 'completed') return 'default'
		if (data?.escalation_status) return 'error'
		return 'success'
	}

	const formatDate = (unix: number | null) => {
		if (!unix) return null
		return moment.unix(unix).format('MMM D, YYYY')
	}

	const renderRow = (label: string, value: any) => (
		<Grid container spacing={1} sx={{ py: 0.4 }}>
			<Grid item xs={3}>
				<Typography variant="body2" color="text.secondary">{label}</Typography>
			</Grid>
			<Grid item xs={9}>
				<Typography variant="body2">{value || '—'}</Typography>
			</Grid>
		</Grid>
	)

	// --- Information Tab ---
	const getMonitorDetails = () => {
		if (!data || Object.keys(data).length === 0) return <EmptyCardComponent />

		const isActive = data?.type !== 'completed'
		const startDate = formatDate(data?.start_date)
		const endDate = formatDate(data?.end_date)
		const dueDate = data?.referral_interval && data?.start_date
			? formatDate(data.start_date + (data.referral_interval * 86400))
			: null

		return (
			<Box>
				{/* Status + dates */}
				<Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
					<Chip
						label={isActive ? 'Active' : 'Completed'}
						size="small"
						color={statusColor(data?.type)}
						variant="outlined"
					/>
					{data?.escalation_status && (
						<Chip label="Escalated" size="small" color="error" variant="filled" />
					)}
					{data?.termination_reason && (
						<Chip label={data.termination_reason.replace(/_/g, ' ')} size="small" variant="outlined" />
					)}
					{isActive && (
						<Box sx={{ flex: 1, textAlign: 'right' }}>
							<Button onClick={() => SetIsStopMonitor(true)} variant="outlined" size="small" color="warning">
								Stop Monitor
							</Button>
						</Box>
					)}
				</Stack>

				{renderRow('Started', startDate)}
				{endDate && renderRow('Ended', endDate)}
				{isActive && data?.referral_interval && renderRow('Review interval', `${data.referral_interval} days`)}
				{isActive && dueDate && renderRow('Review due', dueDate)}
				{data?.tox_med_name && renderRow('Related medication', data.tox_med_name)}
				{data?.cycle_count > 0 && renderRow('Completed cycles', data.cycle_count)}

				{/* What to track */}
				<Divider sx={{ my: 1.5 }} />
				<Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
					Tracking
				</Typography>

				{data?.measurements?.length > 0 && (
					<Box sx={{ mt: 0.5 }}>
						{data.measurements.map((m: string, i: number) => (
							<Typography key={i} variant="body2" sx={{ py: 0.2 }}>{m}</Typography>
						))}
					</Box>
				)}
				{data?.surveys?.length > 0 && (
					<Box sx={{ mt: 0.5 }}>
						{data.surveys.map((s: string, i: number) => (
							<Typography key={i} variant="body2" sx={{ py: 0.2 }}>{s}</Typography>
						))}
					</Box>
				)}
				{(!data?.measurements?.length && !data?.surveys?.length) && (
					<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>No measurements or surveys configured</Typography>
				)}

				{/* Severity update */}
				{isActive && data?.symptom_concept_name && (
					<>
						<Divider sx={{ my: 1.5 }} />
						<Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
							Current Severity
						</Typography>
						<Box sx={{ mt: 0.5 }}>
							<Typography variant="body2" sx={{ fontSize: '0.8rem', mb: 0.5 }}>
								How much does this symptom bother the patient? (1 = not at all, 5 = a lot)
							</Typography>
							<Stack direction="row" spacing={2} alignItems="center" sx={{ maxWidth: 350 }}>
								<Box sx={{ flex: 1, px: 1 }}>
									<Slider
										value={severity}
										min={1} max={5} step={1}
										marks={[1,2,3,4,5].map(v => ({ value: v, label: String(v) }))}
										valueLabelDisplay="auto"
										onChange={(_, val) => setSeverity(val as number)}
										size="small"
									/>
								</Box>
								<Button
									variant="outlined" size="small"
									onClick={handleSaveSeverity}
									disabled={savingSeverity || severity < 1}
									sx={{ minWidth: 60 }}
								>
									{savingSeverity ? <CircularProgress size={14} /> : 'Save'}
								</Button>
							</Stack>
						</Box>
					</>
				)}

				{/* Extend monitor */}
				{isActive && (
					<>
						<Divider sx={{ my: 1.5 }} />
						<Stack direction="row" spacing={1} alignItems="center">
							<Typography variant="body2" color="text.secondary">Extend by</Typography>
							<TextField
								type="number"
								placeholder="Days"
								variant="standard"
								value={noDay}
								sx={{ width: '3.5rem' }}
								InputProps={{ inputProps: { max: 100, min: 1 } }}
								onChange={handleInputChange}
								size="small"
								error={Boolean(daysError)}
							/>
							<Typography variant="body2" color="text.secondary">days</Typography>
							<Button
								variant="outlined"
								size="small"
								disabled={!(parseInt(noDay) > 0)}
								onClick={extendMonitor}
							>
								Save
							</Button>
						</Stack>
						{Boolean(daysError) && (
							<Typography variant="caption" color="error">{daysError}</Typography>
						)}
					</>
				)}
			</Box>
		)
	}

	// --- Evaluation Tab ---
	const getEvaluationDetails = () => {
		const rationale = data?.rationale
		const hasEvalResults = rationale?.stage_termination_criteria && Object.keys(rationale.stage_termination_criteria).length > 0
		const meetsCriteria = rationale?.meets_stage_term_cri || false
		const hasValues = rationale?.measurements && Object.values(rationale.measurements).some((v: any) => v != null)

		return (
			<Box>
				{/* Status + Run button */}
				<Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
					<Stack direction="row" spacing={1} alignItems="center">
						<Typography variant="body2" color="text.secondary">Status:</Typography>
						{hasEvalResults ? (
							<Chip
								icon={meetsCriteria ? <CheckCircleIcon /> : <CancelIcon />}
								label={meetsCriteria ? 'Criteria met' : 'Criteria not met'}
								size="small"
								color={meetsCriteria ? 'success' : 'warning'}
								variant="outlined"
							/>
						) : (
							<Typography variant="body2" color="text.secondary">Not yet evaluated</Typography>
						)}
					</Stack>
					{data?.type !== 'completed' && (
						<Button variant="outlined" size="small" onClick={handleMonitorEval} disabled={loadingEval}>
							{loadingEval ? <CircularProgress size={16} sx={{ mr: 0.5 }} /> : null}
							Run Evaluation
						</Button>
					)}
				</Stack>

				{/* Resolution criteria — always visible from KB */}
				{data?.criteria_names?.length > 0 && (
					<Box sx={{ mb: 1.5 }}>
						<Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
							Resolution Criteria
						</Typography>
						<Box sx={{ mt: 0.5 }}>
							{data.criteria_names.map((name: string, i: number) => (
								<Typography key={i} variant="body2" sx={{ py: 0.2 }}>{name}</Typography>
							))}
						</Box>
					</Box>
				)}

				{/* Current measurement values — only if there are actual readings */}
				{hasValues && (
					<>
						<Divider sx={{ my: 1 }} />
						<Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
							Current Values
						</Typography>
						<Box sx={{ mt: 0.5, mb: 1.5 }}>
							{Object.entries(rationale.measurements)
								.filter(([, val]: [string, any]) => val != null)
								.map(([name, val]: [string, any]) => (
								<Grid container spacing={1} key={name} sx={{ py: 0.2 }}>
									<Grid item xs={4}>
										<Typography variant="body2" color="text.secondary">{name}</Typography>
									</Grid>
									<Grid item xs={8}>
										<Typography variant="body2">{String(val)}</Typography>
									</Grid>
								</Grid>
							))}
						</Box>
					</>
				)}

				{/* Detailed evaluation results — after running evaluation */}
				{hasEvalResults && (
					<>
						<Divider sx={{ my: 1 }} />
						<Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
							Evaluation Results
						</Typography>
						<Grid container spacing={2} sx={{ mt: 0.25 }}>
							<Grid item xs={6}>
								<Typography variant="caption" sx={{ textDecoration: 'underline' }} color="success.main">Met</Typography>
								{Object.entries(rationale.stage_termination_criteria)
									.filter(([, v]: [string, any]) => v.value === true)
									.map(([name, v]: [string, any]) => (
										<Box key={name} sx={{ py: 0.25 }}>
											<Typography variant="body2">{name}</Typography>
											{v.dependents && Object.keys(v.dependents).length > 0 && (
												<Typography variant="caption" color="text.secondary" sx={{ pl: 1 }}>
													{Object.entries(v.dependents).map(([k, d]) => `${k}: ${d}`).join(', ')}
												</Typography>
											)}
										</Box>
									))}
							</Grid>
							<Grid item xs={6}>
								<Typography variant="caption" sx={{ textDecoration: 'underline' }} color="error.main">Not met</Typography>
								{Object.entries(rationale.stage_termination_criteria)
									.filter(([, v]: [string, any]) => v.value === false)
									.map(([name, v]: [string, any]) => (
										<Box key={name} sx={{ py: 0.25 }}>
											<Typography variant="body2">{name}</Typography>
											{v.dependents && Object.keys(v.dependents).length > 0 && (
												<Typography variant="caption" color="text.secondary" sx={{ pl: 1 }}>
													{Object.entries(v.dependents).map(([k, d]) => `${k}: ${d}`).join(', ')}
												</Typography>
											)}
										</Box>
									))}
							</Grid>
						</Grid>
					</>
				)}
			</Box>
		)
	}

	return <>
		{open &&
			<Draggable ref={monitorRef} defaultPosition={{ x: 80, y: 0 }} handle='.monitor-details-dialog'>
				<Dialog
					id={`monitor-${input?.id}`}
					fullWidth={true}
					maxWidth="md"
					open={open}
					onClose={handleClose}
					hideBackdrop
					disableScrollLock
					onKeyUp={(e) => {
						e.preventDefault();
						if (e.key === 'Enter' && (parseInt(noDay) > 0))
							extendMonitor()
					}}
					sx={{
						position: "absolute",
						left: '10rem',
						top: '5rem'
					}}
				>
					<DialogTitle sx={{ padding: '15px 10px 0px 20px' }} classes={{ root: 'monitor-details-dialog' }}>
						<DialogTitleHeaderComponent
							title={data?.display_name || data?.name}
							handleDialogClose={handleClose}
						/>
					</DialogTitle>
					<DialogContent className={classes.dialogContentStyle}>
						<Box mb={1}>
							<div className={classes.root}>
								<AppBar position="static" color="transparent">
									<Tabs value={value} onChange={handleChange} variant="standard">
										{configItem?.tabs?.map((c: any) => (
											<Tab
												key={`config-${c.title}`}
												label={c.title}
												{...a11yProps(c.index)}
												disabled={false}
											/>
										))}
									</Tabs>
								</AppBar>
								<Box className={classes.linearProgressContainer}>
									{loading === true && <LinearProgress />}
									<Box className={classes.dialogHeaderBox} sx={{padding:'1.5rem 1rem'}}>
										{configItem?.tabs?.map((c: any, i: any) => (
											<Box pl={2} pr={2} key={`compset-${i}`}>
												<TabPanel value={value} index={c.index}>
													{i === 0
														? getMonitorDetails()
														: getEvaluationDetails()
													}
												</TabPanel>
											</Box>
										))}
									</Box>
								</Box>
							</div>
						</Box>
					</DialogContent>
				</Dialog>
			</Draggable>
		}
		{isStopMonitor &&
			<Dialog open={isStopMonitor} onClose={handleConfirmMonitorClose} maxWidth="xs" fullWidth>
				<DialogTitle sx={{ pb: 1 }}>Stop Monitor</DialogTitle>
				<DialogContent>
					<Typography variant="body2" sx={{ mb: 1.5 }}>Select a reason:</Typography>
					<RadioGroup value={stopReason} onChange={(e) => setStopReason(e.target.value)}>
						<FormControlLabel value="resolved" control={<Radio size="small" />} label="Resolved" />
						<FormControlLabel value="clinical_decision" control={<Radio size="small" />} label="Clinical decision" />
						<FormControlLabel value="patient_request" control={<Radio size="small" />} label="Patient request" />
						<FormControlLabel value="worsened" control={<Radio size="small" color="error" />}
							label={<Typography variant="body2" color="error.main">Escalate to provider (worsened)</Typography>}
						/>
					</RadioGroup>
					<TextField
						fullWidth size="small" variant="outlined" placeholder="Optional note..."
						value={stopNote} onChange={(e) => setStopNote(e.target.value)}
						sx={{ mt: 1.5 }}
						multiline rows={2}
					/>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleConfirmMonitorClose} size="small">Cancel</Button>
					<Button onClick={confirmMonitor} variant="contained" size="small"
						color={stopReason === 'worsened' ? 'error' : 'primary'}
					>
						{stopReason === 'worsened' ? 'Escalate' : 'Stop Monitor'}
					</Button>
				</DialogActions>
			</Dialog>
		}
	</>
}

export default withStyles(stylesheet)(MonitorDialogComponent)
